import { useState } from 'react';

import { faCircleXmark } from '@fortawesome/pro-light-svg-icons';
import { useTranslation } from 'react-i18next';

import { formatByLocale } from 'utils/date';

import { ClientAbsence } from 'RestAPIs/absence';

import { Box, Button } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';

import { DataGrid, Dialog } from 'components/shared';

import {
  useGetClientAbsenceReasons,
  useGetClientAbsences
} from '../../queries';
import AddAbsenceDialog from '../AddAbsenceDialog';
import DeleteAbsencePeriodDialog from './DeleteAbsencePeriodDialog';

type Props = {
  appointmentStart: string;
  clientId: string | undefined;
  clientName: string;
  handleClose: () => void;
  open: boolean;
};

export default function ClientAbsencePeriodsDialog({
  appointmentStart,
  clientId,
  clientName,
  handleClose,
  open
}: Props) {
  const { t } = useTranslation('common');

  const [openAddAbsenceDialog, setOpenAddAbsenceDialog] = useState(false);
  const [openDeleteAbsenceDialog, setOpenDeleteAbsenceDialog] = useState(false);
  const [editData, setEditData] = useState<ClientAbsence | undefined>();

  const dateSortComparator = (a: Date | string, b: Date | string) => {
    if (!a || !b) return 0;
    return dayjs(a).isBefore(dayjs(b)) ? -1 : 1;
  };

  const { data: absenceReasons } = useGetClientAbsenceReasons({
    enabled: open
  });
  const { data, isLoading } = useGetClientAbsences(
    { clientId: clientId as string },
    { enabled: !!clientId && open }
  );

  const columns: GridColDef[] = [
    {
      field: 'startDate',
      headerName: t('startDate'),
      sortComparator: dateSortComparator
    },
    {
      field: 'endDate',
      headerName: t('endDate'),
      sortComparator: dateSortComparator
    },
    { field: 'allAppointments', headerName: t('allAppointments') },
    {
      field: 'reasonId',
      headerName: t('reasonAbsence'),
      flex: 1
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1
    }
  ];

  const rows =
    data?.map(
      ({
        absentAppointments,
        clientAbsenceId,
        reasonId,
        start,
        end,
        description
      }) => ({
        id: clientAbsenceId,
        reasonId:
          absenceReasons?.find(({ id }) => id === reasonId)?.name ??
          t('unknown'),
        allAppointments: absentAppointments ? t('no') : t('yes'),
        startDate: formatByLocale(new Date(start)),
        endDate: end == null ? '' : formatByLocale(new Date(end)),
        description
      })
    ) ?? [];

  function setAbsenceById(id) {
    const absence = data?.find(({ clientAbsenceId }) => clientAbsenceId === id);
    setEditData(absence);
  }

  function handleEditClick(id) {
    setAbsenceById(id);
    setOpenAddAbsenceDialog(true);
  }

  function handleDeleteClick(id) {
    setAbsenceById(id);
    setOpenDeleteAbsenceDialog(true);
  }

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      handleClose={handleClose}
      onMouseOver={(e) => e.stopPropagation()}
      title={`Afwezigheden: ${clientName}`}
    >
      <Box sx={{ width: '768px' }}>
        <Button
          variant="contained"
          color="dark"
          onClick={() => {
            setEditData(undefined);
            setOpenAddAbsenceDialog(true);
          }}
          sx={{ display: 'block', ml: 'auto' }}
        >
          {t('addTimes')}
        </Button>
        <DataGrid
          loading={isLoading}
          columns={columns}
          rows={rows}
          onEditClick={handleEditClick}
          customActions={[
            {
              tooltipTitle: 'Verwijderen',
              icon: faCircleXmark,
              onClick: handleDeleteClick
            }
          ]}
        />
      </Box>
      <AddAbsenceDialog
        appointmentStart={appointmentStart}
        clientId={clientId}
        clientName={clientName}
        editData={editData}
        handleClose={() => {
          setEditData(undefined);
          setOpenAddAbsenceDialog(false);
        }}
        open={openAddAbsenceDialog}
        title={t('activityPlanning.absenceDialogTitle', { clientName })}
      />
      <DeleteAbsencePeriodDialog
        clientAbsence={editData}
        clientId={clientId}
        handleClose={() => {
          setEditData(undefined);
          setOpenDeleteAbsenceDialog(false);
        }}
        open={openDeleteAbsenceDialog}
        title={`${t('activityPlanning.editAbsencePeriod')}: ${clientName}`}
      />
    </Dialog>
  );
}
